import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { FileText, X } from 'lucide-react';
import Modal from '../Modal';
import FileDropzone from '../FileDropzone';
import { expertAgentApi, getExpertErrorMessage, isRequestCancelled } from '../../lib/domain-expert-api';
import { btnPrimary, btnSecondary } from './ui';

const ACCEPTED_EXTENSIONS = ['.pdf', '.txt', '.md'];
const MAX_FILES = 10;

const hasAcceptedExtension = (file: File) => ACCEPTED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface TrainingUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  readOnly: boolean;
}

export default function TrainingUploadModal({ isOpen, onClose, readOnly }: TrainingUploadModalProps) {
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFiles([]);
    setNotice(null);
    setError(null);
  };

  const mutation = useMutation({
    mutationFn: () => expertAgentApi.uploadDocuments(files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expert', 'documents'] });
      toast.success('Documents added. They will be ready in a few minutes.');
      reset();
      onClose();
    },
    onError: (err) => {
      if (isRequestCancelled(err)) return;
      setError(getExpertErrorMessage(err, "We couldn't upload these files. Please try again."));
    },
  });

  const handleFilesSelected = useCallback(
    (selected: File[]) => {
      const accepted = selected.filter(hasAcceptedExtension);
      const rejected = selected.filter((file) => !hasAcceptedExtension(file));
      const merged = [...files];
      accepted.forEach((file) => {
        if (!merged.some((f) => f.name === file.name && f.size === file.size)) merged.push(file);
      });
      const messages: string[] = [];
      if (rejected.length > 0) {
        messages.push(`Skipped ${rejected.map((f) => f.name).join(', ')}. Only PDF, TXT and MD files are supported.`);
      }
      if (merged.length > MAX_FILES) messages.push(`You can add up to ${MAX_FILES} files at a time.`);
      setError(null);
      setNotice(messages.length ? messages.join(' ') : null);
      setFiles(merged.slice(0, MAX_FILES));
    },
    [files],
  );

  const handleClose = () => {
    if (mutation.isLoading) return;
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload documents"
      size="md"
      footer={
        <>
          <button type="button" onClick={handleClose} disabled={mutation.isLoading} className={`${btnSecondary} mt-3 sm:mt-0`}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={files.length === 0 || mutation.isLoading || readOnly}
            className={btnPrimary}
          >
            {mutation.isLoading ? 'Uploading…' : files.length > 1 ? `Upload ${files.length} files` : 'Upload'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Add PDFs or text files your agent should learn from. PDF, TXT or MD, <span className="font-bold">up to {MAX_FILES} files at a time</span>.
        </p>
        <FileDropzone
          onFilesSelected={handleFilesSelected}
          acceptedTypes={ACCEPTED_EXTENSIONS}
          maxFiles={MAX_FILES}
          disabled={mutation.isLoading || readOnly}
          label="Drag & drop PDF, TXT or MD files here"
        />
        {notice && <p className="text-sm text-amber-700">{notice}</p>}
        {files.length > 0 && (
          <ul className="divide-y divide-gray-200 border border-gray-200">
            {files.map((file) => (
              <li key={`${file.name}-${file.size}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <span className="truncate text-gray-800">{file.name}</span>
                  <span className="flex-shrink-0 text-xs text-gray-500">{formatSize(file.size)}</span>
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  disabled={mutation.isLoading}
                  onClick={() => setFiles((prev) => prev.filter((f) => f !== file))}
                  className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
